import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { useLocale } from '@parentpath/i18n';
import type {
  Guide,
  GuideCategory,
  GuideTerm,
  GuideFAQ,
  GuideRating,
  GuideSearchResult,
} from '../types';

// =====================================================
// GUIDES
// =====================================================

export function useGuides() {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guides', locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('*')
        .eq('is_active', true)
        .eq('locale', locale)
        .order('order_index');

      if (error) throw error;
      return data as Guide[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useGuide(slug: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide', slug, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .eq('locale', locale)
        .single();

      if (error) throw error;
      return data as Guide;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

// =====================================================
// CATEGORIES
// =====================================================

export function useGuideCategories(guideId: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-categories', guideId, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guide_categories')
        .select('*')
        .eq('guide_id', guideId)
        .eq('is_active', true)
        .eq('locale', locale)
        .order('order_index');

      if (error) throw error;
      return data as GuideCategory[];
    },
    enabled: !!guideId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGuideCategoriesBySlug(guideSlug: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-categories-by-slug', guideSlug, locale],
    queryFn: async () => {
      // First get the guide
      const { data: guide, error: guideError } = await supabase
        .from('guides')
        .select('id')
        .eq('slug', guideSlug)
        .eq('is_active', true)
        .eq('locale', locale)
        .single();

      if (guideError) throw guideError;

      // Then get categories
      const { data, error } = await supabase
        .from('guide_categories')
        .select('*')
        .eq('guide_id', guide.id)
        .eq('is_active', true)
        .eq('locale', locale)
        .order('order_index');

      if (error) throw error;
      return data as GuideCategory[];
    },
    enabled: !!guideSlug,
    staleTime: 5 * 60 * 1000,
  });
}

// =====================================================
// TERMS (Fachbegriffe)
// =====================================================

export function useGuideTerms(guideId: string, categoryId?: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-terms', guideId, categoryId, locale],
    queryFn: async () => {
      let query = supabase
        .from('guide_terms')
        .select(`
          *,
          category:guide_categories(id, slug, name)
        `)
        .eq('guide_id', guideId)
        .eq('is_active', true)
        .eq('locale', locale)
        .order('term');

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as GuideTerm[];
    },
    enabled: !!guideId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGuideTerm(termId: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-term', termId, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guide_terms')
        .select(`
          *,
          category:guide_categories(id, slug, name),
          guide:guides(id, slug, title, is_premium)
        `)
        .eq('id', termId)
        .eq('locale', locale)
        .single();

      if (error) throw error;
      return data as GuideTerm;
    },
    enabled: !!termId,
  });
}

// =====================================================
// FAQs
// =====================================================

export function useGuideFAQs(guideId: string, categoryId?: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-faqs', guideId, categoryId, locale],
    queryFn: async () => {
      let query = supabase
        .from('guide_faqs')
        .select(`
          *,
          category:guide_categories(id, slug, name)
        `)
        .eq('guide_id', guideId)
        .eq('is_active', true)
        .eq('locale', locale)
        .order('question');

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as GuideFAQ[];
    },
    enabled: !!guideId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGuideFAQ(faqId: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-faq', faqId, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guide_faqs')
        .select(`
          *,
          category:guide_categories(id, slug, name),
          guide:guides(id, slug, title, is_premium)
        `)
        .eq('id', faqId)
        .eq('locale', locale)
        .single();

      if (error) throw error;
      return data as GuideFAQ;
    },
    enabled: !!faqId,
  });
}

// =====================================================
// SEARCH
// =====================================================

export function useGuideSearch(query: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['guide-search', query, locale],
    queryFn: async () => {
      if (!query || query.length < 2) return [];

      const searchQuery = query.toLowerCase().trim();
      const results: GuideSearchResult[] = [];

      // Search in terms
      const { data: terms, error: termsError } = await supabase
        .from('guide_terms')
        .select(`
          id,
          term,
          explanation,
          guide:guides(slug, title, is_premium),
          category:guide_categories(slug, name)
        `)
        .eq('is_active', true)
        .eq('locale', locale)
        .or(`term.ilike.%${searchQuery}%,explanation.ilike.%${searchQuery}%,keywords.cs.{${searchQuery}}`)
        .limit(5);

      if (!termsError && terms) {
        for (const term of terms) {
          const guide = term.guide as unknown as { slug: string; title: string; is_premium: boolean };
          const category = term.category as unknown as { slug: string; name: string } | null;

          results.push({
            id: term.id,
            type: 'term',
            title: term.term,
            preview: term.explanation.substring(0, 100) + (term.explanation.length > 100 ? '...' : ''),
            breadcrumbs: [guide?.title || '', category?.name || ''].filter(Boolean),
            guide_slug: guide?.slug || '',
            category_slug: category?.slug || '',
            is_premium: guide?.is_premium || false,
          });
        }
      }

      // Search in FAQs
      const { data: faqs, error: faqsError } = await supabase
        .from('guide_faqs')
        .select(`
          id,
          question,
          short_answer,
          guide:guides(slug, title, is_premium),
          category:guide_categories(slug, name)
        `)
        .eq('is_active', true)
        .eq('locale', locale)
        .or(`question.ilike.%${searchQuery}%,short_answer.ilike.%${searchQuery}%,keywords.cs.{${searchQuery}}`)
        .limit(5);

      if (!faqsError && faqs) {
        for (const faq of faqs) {
          const guide = faq.guide as unknown as { slug: string; title: string; is_premium: boolean };
          const category = faq.category as unknown as { slug: string; name: string } | null;

          results.push({
            id: faq.id,
            type: 'faq',
            title: faq.question,
            preview: faq.short_answer.substring(0, 100) + (faq.short_answer.length > 100 ? '...' : ''),
            breadcrumbs: [guide?.title || '', category?.name || ''].filter(Boolean),
            guide_slug: guide?.slug || '',
            category_slug: category?.slug || '',
            is_premium: guide?.is_premium || false,
          });
        }
      }

      // Sort by relevance (exact match first, then partial)
      results.sort((a, b) => {
        const aExact = a.title.toLowerCase().includes(searchQuery) ? 0 : 1;
        const bExact = b.title.toLowerCase().includes(searchQuery) ? 0 : 1;
        return aExact - bExact;
      });

      // Return top 3 results
      return results.slice(0, 3);
    },
    enabled: query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// =====================================================
// RATINGS
// =====================================================

export function useGuideRating(contentType: 'term' | 'faq', contentId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['guide-rating', contentType, contentId, user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('guide_ratings')
        .select('*')
        .eq('user_id', user.id)
        .eq('content_type', contentType)
        .eq('content_id', contentId)
        .maybeSingle();

      if (error) throw error;
      return data as GuideRating | null;
    },
    enabled: !!user && !!contentId,
  });
}

export function useRateContent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      contentType,
      contentId,
      isHelpful,
    }: {
      contentType: 'term' | 'faq';
      contentId: string;
      isHelpful: boolean;
    }) => {
      if (!user) throw new Error('Nicht eingeloggt');

      const { data, error } = await supabase
        .from('guide_ratings')
        .upsert(
          {
            user_id: user.id,
            content_type: contentType,
            content_id: contentId,
            is_helpful: isHelpful,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,content_type,content_id',
          }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['guide-rating', variables.contentType, variables.contentId],
      });
      // Also invalidate the content to update counts
      if (variables.contentType === 'term') {
        queryClient.invalidateQueries({ queryKey: ['guide-term', variables.contentId] });
        queryClient.invalidateQueries({ queryKey: ['guide-terms'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['guide-faq', variables.contentId] });
        queryClient.invalidateQueries({ queryKey: ['guide-faqs'] });
      }
    },
  });
}

export function useDeleteRating() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      contentType,
      contentId,
    }: {
      contentType: 'term' | 'faq';
      contentId: string;
    }) => {
      if (!user) throw new Error('Nicht eingeloggt');

      const { error } = await supabase
        .from('guide_ratings')
        .delete()
        .eq('user_id', user.id)
        .eq('content_type', contentType)
        .eq('content_id', contentId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['guide-rating', variables.contentType, variables.contentId],
      });
    },
  });
}

// =====================================================
// VIEW COUNT
// =====================================================

export function useIncrementViewCount() {
  return useMutation({
    mutationFn: async ({
      contentType,
      contentId,
    }: {
      contentType: 'term' | 'faq';
      contentId: string;
    }) => {
      const { error } = await supabase.rpc('increment_guide_view_count', {
        p_content_type: contentType,
        p_content_id: contentId,
      });

      if (error) throw error;
    },
  });
}

// =====================================================
// SUBMISSIONS
// =====================================================

export function useMySubmissions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-guide-submissions', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('guide_submissions')
        .select(`
          *,
          guide:guides(slug, title)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useSubmitSuggestion() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      submissionType,
      suggestedGuideId,
      suggestedCategory,
      content,
    }: {
      submissionType: 'term' | 'faq';
      suggestedGuideId?: string;
      suggestedCategory?: string;
      content: { term?: string; explanation?: string; question?: string; answer?: string };
    }) => {
      if (!user) throw new Error('Nicht eingeloggt');

      const { data, error } = await supabase
        .from('guide_submissions')
        .insert({
          user_id: user.id,
          submission_type: submissionType,
          suggested_guide_id: suggestedGuideId || null,
          suggested_category: suggestedCategory || null,
          content,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-guide-submissions'] });
    },
  });
}
